import type { JsonPathResolution, IdempotencyKeyPreview } from '../types/stripe';

/**
 * Resolve a JSONPath against sample data
 */
export function resolveJsonPath(sample: unknown, path?: string): JsonPathResolution {
	if (!sample || !path) {
		return { path: path || '', resolved: false, error: 'No sample or path' };
	}

	try {
		// Simple JSONPath resolution for now - can be enhanced with jsonpath-plus later
		const pathParts = path.replace(/^\$\.?/, '').split('.');
		let current: any = sample;
		
		for (const part of pathParts) {
			if (current && typeof current === 'object' && part in current) {
				current = current[part];
			} else {
				return { path, resolved: false, error: `Path not found: ${part}` };
			}
		}
		
		return { path, resolved: true, value: current };
	} catch (error: any) {
		return { path, resolved: false, error: error.message };
	}
}

/**
 * Generate a stable hash for idempotency key generation
 * This matches the backend algorithm
 */
function stableInputHash(obj: unknown): string {
	if (!obj) return '';
	
	try {
		// Sort keys for consistent hashing
		const sorted = Object.keys(obj).sort().reduce((result: any, key) => {
			result[key] = obj[key as keyof typeof obj];
			return result;
		}, {});
		
		const jsonString = JSON.stringify(sorted);
		// Simple hash function - in production, use a proper hashing library
		let hash = 0;
		for (let i = 0; i < jsonString.length; i++) {
			const char = jsonString.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return Math.abs(hash).toString(16);
	} catch {
		return '';
	}
}

/**
 * Preview the idempotency key that will be generated
 */
export function previewIdempotencyKey({
	executionId,
	nodeId,
	operation,
	resolvedInputs,
	template,
}: {
	executionId: string;
	nodeId: string;
	operation: string;
	resolvedInputs: unknown;
	template?: string;
}): IdempotencyKeyPreview {
	if (template?.trim()) {
		return {
			key: template,
			inputs: resolvedInputs as Record<string, any>,
			hash: '',
		};
	}

	const hash = stableInputHash(resolvedInputs);
	const key = `${executionId}:${nodeId}:${operation}:${hash}`;

	return {
		key,
		inputs: resolvedInputs as Record<string, any>,
		hash,
	};
}

/**
 * Convert dollars to cents for display
 */
export function dollarsToCents(dollars: number): number {
	return Math.round(dollars * 100);
}

/**
 * Convert cents to dollars for display
 */
export function centsToDollars(cents: number): number {
	return cents / 100;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = 'usd'): string {
	const formatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 2,
	});
	
	return formatter.format(amount);
}

/**
 * Validate that only one of two mutually exclusive fields is provided
 */
export function validateMutualExclusion(
	field1: string | undefined,
	field2: string | undefined,
	field1Name: string,
	field2Name: string
): { valid: boolean; error?: string } {
	if (field1 && field2) {
		return {
			valid: false,
			error: `Provide either ${field1Name} or ${field2Name} (not both)`,
		};
	}
	
	if (!field1 && !field2) {
		return {
			valid: false,
			error: `Provide either ${field1Name} or ${field2Name}`,
		};
	}
	
	return { valid: true };
}

/**
 * Get required fields for a specific operation
 */
export function getRequiredFieldsForOperation(operation: string): string[] {
	const operationMap: Record<string, string[]> = {
		create_payment_intent: ['amountPath'],
		confirm_payment_intent: ['paymentIntentIdPath'],
		refund_payment: ['paymentIntentIdPath', 'chargeIdPath'], // One of these
		create_customer: ['customerEmailPath'],
		attach_payment_method: ['attachCustomerIdPath', 'attachPaymentMethodIdPath'],
		create_subscription: ['subCustomerIdPath', 'priceIdPath'],
		cancel_subscription: ['subscriptionIdPath'],
		create_invoice: ['invoiceCustomerIdPath'],
		finalize_invoice: ['invoiceIdPath'],
		send_invoice: ['invoiceIdPath'],
	};
	
	return operationMap[operation] || [];
}

/**
 * Check if a field is required for the current operation
 */
export function isFieldRequired(fieldName: string, operation: string): boolean {
	const requiredFields = getRequiredFieldsForOperation(operation);
	return requiredFields.includes(fieldName);
}
